import { useId } from 'react';
import {
  mergePickerItems,
  normalizeClassifiedItems,
  type PickerItem,
} from '@/components/modals/formPickerItems';
import React, { useState, useRef } from 'react';
import { Field, Form, Formik } from 'formik';
import { Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../../../../../components/ui/Dialog';
import { Button } from '../../../../../components/ui/Button';
import * as Yup from 'yup';
import { InputField } from '../../../../../components/formFields/InputField';
import Bar from '../../../../../components/elements/Bar';
import PickValueWithClassificationModal from '../../../../../components/modals/PickValueWithClassificationModal';
import Label from '@/components/Label';
import FormList from '../../../../../components/elements/FormList';

type TechnicalReportItemSetModalProps = {
  visible: boolean;
  title: string;
  formProps?: any;
  onSubmit: (id: any, values: any) => void;
  onClose: () => void;
  classification?: any[];
};

function TechnicalReportItemSetModalForm({
  visible,
  title,
  formProps,
  onSubmit,
  onClose,
}: TechnicalReportItemSetModalProps) {
  const [visibleAddTechnicalReportItemDialog, setVisibleAddTechnicalReportItemDialog] =
    useState(false);

  const ValidationSchema = Yup.object().shape({
    title: Yup.string().nullable().required('Povinné pole'),
  });

  const pickerSession = useId();
  const formRef = useRef<any>(null);

  const handleDeleteTechnicalReportItem = (id: any) => {
    if (!formRef?.current) return null;

    const newValues = formRef.current.values.technical_report_item_set.filter(
      (ele: any) => ele.id !== id
    );

    formRef.current.setFieldValue('technical_report_item_set', newValues);

    return null;
  };

  const handleAddTechnicalReportItem = async (items: PickerItem[]) => {
    await formRef.current.setFieldValue(
      'technical_report_item_set',
      mergePickerItems(formRef.current.values.technical_report_item_set, items)
    );
  };

  const onSubmitButton = () => {
    if (!formRef?.current) return null;
    formRef.current.submitForm();

    return null;
  };

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Formik
            innerRef={formRef}
            enableReinitialize
            validationSchema={ValidationSchema}
            initialValues={{
              title: formProps?.title || null,
              technical_report_item_set: normalizeClassifiedItems(
                formProps?.technical_report_item_set || [],
                'technical_report_item'
              ),
            }}
            onSubmit={(values) => {
              onSubmit(formProps?.id, {
                ...values,
                technical_report_item_set: values.technical_report_item_set.map(
                  (technical_report_item: any) => technical_report_item.id
                ),
              });
            }}
          >
            {(props) => {
              const { values } = props;
              return (
                <>
                  <Form className="space-y-1">
                    <div>
                      <Label name="title" title="Název" required />
                      <Field
                        name="title"
                        value={values.title || null}
                        placeholder="Název"
                        component={InputField}
                        data-testid="tech-report-item-set-modal-title-input"
                      />
                    </div>

                    <div className="mb-1">
                      <Bar
                        text="Položky technické zprávy"
                        content={[
                          <Plus
                            key={1}
                            className="h-4 w-4"
                            onClick={() => setVisibleAddTechnicalReportItemDialog(true)}
                            data-testid="tech-report-item-set-modal-add-item-btn"
                          />,
                        ]}
                      />
                      <FormList
                        data={values.technical_report_item_set}
                        onDelete={handleDeleteTechnicalReportItem}
                      />
                    </div>
                  </Form>
                  <PickValueWithClassificationModal
                    multiple
                    sessionKey={pickerSession}
                    visible={visibleAddTechnicalReportItemDialog}
                    url="left-panel-technical-report-item"
                    pickerUrl="technical-report-item/left-panel-picker/"
                    onClose={() => setVisibleAddTechnicalReportItemDialog(false)}
                    onSubmit={handleAddTechnicalReportItem}
                    alreadySelectedValues={values.technical_report_item_set}
                  />
                </>
              );
            }}
          </Formik>
        </DialogBody>
        <DialogFooter>
          <Button
            key={1}
            variant="outline"
            onClick={() => onClose()}
            data-testid="tech-report-item-set-modal-close-btn"
          >
            Zavřít
          </Button>
          <Button
            key={2}
            onClick={() => onSubmitButton()}
            data-testid="tech-report-item-set-modal-submit-btn"
          >
            Potvrdit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TechnicalReportItemSetModal(
  props: React.ComponentProps<typeof TechnicalReportItemSetModalForm>
) {
  return props.visible ? (
    <TechnicalReportItemSetModalForm
      key={`${props.formProps?.id ?? 'create'}:${props.formProps?.id_version ?? ''}`}
      {...props}
    />
  ) : null;
}

export default TechnicalReportItemSetModal;
